<template>
  <div v-if="config !== `${orgType}_disabled`">
    <div class="row">
      <div class="col-md-8" v-show="!showForm">
        <div class="ibox">
          <div class="ibox-title border-bottom-none m-b-none">
            <h1>
              <span v-if="profileName !== ''">{{ profileName }}'s</span>
              {{ orgName }} Membership
              <span v-if="!requiresValidCard">(optional)</span>
            </h1>
            <p class="m-t-xs font-small">
              {{ promptText }}
            </p>
            <div
              class="border-top-bottom grid grid-cols-3 sm-grid-cols-5 p-x-xs m-t-lg"
            >
              <h2 class="upper-label sm-col-span-2">{{ orgName }} ID</h2>
              <h2 class="upper-label sm-col-span-2">Valid until</h2>
              <h2 class="upper-label m-l-auto">Actions</h2>
            </div>
          </div>
          <div class="ibox-content">
            <spinner v-if="loading"></spinner>
            <div v-else>
              <div
                class="grid grid-cols-3 sm-grid-cols-5"
                v-for="membership in memberships"
                :key="membership.id"
              >
                <div class="flex-col sm-col-span-2">
                  <h4 class="font-semi m-y-none">
                    <template
                      v-if="membership.id_card && membership.id_card.url"
                    >
                      <a
                        :href="membership.id_card.url"
                        target="_blank"
                        class="flex align-items-center"
                      >
                        {{ membership.public_id }}
                        <i
                          class="fa-solid fa-up-right-from-square m-l-xxs"
                          style="font-size: 8px"
                          aria-hidden="true"
                        ></i>
                      </a>
                    </template>
                    <template v-else>
                      {{ membership.public_id }}
                    </template>
                  </h4>
                  <p class="font-small">
                    {{ membership.membership_type }}
                  </p>
                </div>
                <div class="flex sm-col-span-2 gap-x-xs gap-x-xxs-xs-only">
                  <div>
                    {{ readableExpires(membership.expires_on) }}
                  </div>
                  <div class="" v-html="badgeFor(membership)"></div>
                </div>
                <div class="m-l-auto">
                  <a
                    class="text-danger"
                    @click.prevent="confirmDelete(membership)"
                  >
                    <i class="fa-regular fa-trash-can color-red-600"></i>
                  </a>
                </div>
              </div>
              <div v-if="!hasValidCard">
                <p
                  class="alert alert-warning m-t-lg"
                  v-if="requiresValidCard && hasOldCard"
                >
                  {{ profileName }} has an expired {{ orgName }} card. Please
                  renew {{ profileName }}'s membership, then input your new
                  info. <br /><br /><strong>Note:</strong> {{ orgName }} info
                  recorded in WrestlingIQ is not editable. Please use the button
                  below to enter your {{ orgName }} membership info, even if
                  just your expiration date changed.
                </p>
                <p
                  class="alert alert-warning m-t-lg"
                  v-else-if="requiresValidCard"
                >
                  {{ profileName }} needs an active {{ orgName }} membership on
                  file to train with {{ teamName }}. Please purchase one and
                  enter your membership info here.
                </p>
                <p class="alert alert-warning m-t-lg" v-else-if="hasOldCard">
                  {{ profileName }} has an expired {{ orgName }} membership on
                  file. If {{ profileName }}'s membership has renewed, please
                  input your new info. <br /><br /><strong>Note:</strong>
                  {{ orgName }} info recorded in WrestlingIQ is not editable.
                  Please use the button below to enter your
                  {{ orgName }} membership info, even if just your expiration
                  date changed.
                </p>
                <p v-else>
                  {{ profileName }} does not have an active
                  {{ orgName }} Membership on file. If {{ profileName }} has a
                  membership, please enter that info here.
                </p>
              </div>
              <div class="m-t-lg flex">
                <button
                  :data-cy="`${orgType}-btn`"
                  class="btn btn-success"
                  @click="toggleForm"
                >
                  <i class="fa-solid fa-plus"></i> {{ orgName }} Membership
                </button>
                <button
                  data-cy="submit"
                  class="btn btn-primary align-self-flex-end m-l-auto"
                  :disabled="!hasValidCard && requiresValidCard"
                  @click="$emit('membership-completed')"
                  v-if="!redirect"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showForm">
        <insurance-membership-form
          :wrestler-id="wrestlerId"
          :profile-name="profileName"
          :config="config"
          :access-key="accessKey"
          :endpoint="endpoint"
          :most-recent-id="mostRecentId"
          :cloudfront-base-url="cloudfrontBaseUrl"
          :org-type="orgType"
          :org-name="orgName"
          v-on:cancel-form="toggleForm"
          v-on:membership-created="addMembership"
        ></insurance-membership-form>
      </div>
      <div class="col-md-4">
        <div class="ibox p-lg">
          <component :is="`${orgType}-logo`" class=""></component>
          <h2 class="font-heavy org-blue m-t-md" v-html="orgCta"></h2>
          <p class="m-t-md">
            {{ orgDescription }}
          </p>
          <ul class="list-unstyled org-features m-t-m">
            <li v-for="feature in orgFeatures" :key="feature" class="m-b-xs">
              <i class="fa-solid fa-check"></i> {{ feature }}
            </li>
            <li v-if="requiresValidCard" class="m-b-xs">
              <i class="fa-solid fa-check"></i> Compliance with {{ teamName }}'s
              {{ orgName }} Club Charter. All athletes must have an active
              membership to practice
            </li>
          </ul>
          <a
            target="blank"
            :href="purchaseUrl"
            class="btn usaw-btn btn-block m-t-lg"
          >
            Purchase membership
          </a>
          <p class="text-center font-small m-t-sm">
            You will be redirected to {{ orgName }}'s website to
            <a :href="helpUrl" target="_blank" class="text-underline"
              >purchase your membership</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from 'sweetalert2';
import { errorableMix } from './mixins/errorable_mix';
import UsawLogo from './svgs/usaw_logo.vue';
import AauLogo from './svgs/aau_logo.vue';
import Spinner from './spinner.vue';
import InsuranceMembershipForm from './insurance_membership_form.vue';
import { photoUploadMix } from './mixins/photo_upload_mix';

export default {
  name: 'membership-manager',
  components: {
    InsuranceMembershipForm,
    Spinner,
    UsawLogo,
    AauLogo,
  },
  mixins: [errorableMix, photoUploadMix],
  props: {
    wrestlerId: {
      type: Number,
      required: true,
    },
    mostRecentId: {
      type: String,
      default: '',
    },
    mostRecentExpired: {
      type: String,
      default: '',
    },
    buttonCta: {
      type: String,
      default: 'Save',
    },
    config: {
      type: String,
      default: 'disabled',
    },
    teamName: {
      type: String,
      default: '',
    },
    profileName: {
      type: String,
      default: '',
    },
    redirect: {
      type: Boolean,
      default: false,
    },
    orgType: {
      type: String,
      required: true,
      validator(value) {
        return ['usaw', 'aau'].includes(value);
      },
    },
    orgName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      memberships: [],
      showForm: false,
      orgConfigs: {
        usaw: {
          description:
            'As the national governing body of wrestling in the United States, USA Wrestling charters clubs and sanctions over 2100 competitions a year for their members.',
          features: [
            'Secondary sports accident coverage for injuries sustained while wrestling',
            'Participation in local, regional, and national competitions',
          ],
          purchaseUrl: 'https://www.usawmembership.com',
          helpUrl: 'https://www.usawmembership.com/help/category/3/question/14',
        },
        aau: {
          description:
            'Nearly 800,000 AAU members compete in one of their 50+ sport programs across the nation.',
          features: [
            'Youth Members may participate in 45 sports at local, regional, national events',
            'Only one youth membership is needed to participate in any sport',
            'Up to $100,000 in sports accident insurance when participating in AAU Supervised Practices or licensed events',
          ],
          purchaseUrl: 'https://aausports.org/Membership',
          helpUrl: 'https://aausports.org/Membership',
        },
      },
    };
  },
  computed: {
    hasValidCard() {
      return _.find(this.memberships, (membership) => moment().isBefore(membership.expires_on, 'day'));
    },
    hasOldCard() {
      return this.memberships.length > 0;
    },
    requiresValidCard() {
      return (
        this.config === `${this.orgType}_require_all`
        || this.config === `${this.orgType}_require_id_and_expires`
      );
    },
    promptText() {
      switch (this.config) {
        case `${this.orgType}_optional`:
          return `If you have a valid ${this.orgName} Membership, please enter it`;
        case `${this.orgType}_require_all`:
        case `${this.orgType}_require_id_and_expires`:
          return `In order to train with ${this.teamName} an active ${this.orgName} Membership is required`;
        default:
          return '';
      }
    },
    orgCta() {
      switch (this.config) {
        case `${this.orgType}_require_all`:
        case `${this.orgType}_require_id_and_expires`:
          return `What is ${this.orgName} and why does ${this.profileName} need a membership?`;
        default:
          return `Why would ${this.profileName} want a ${this.orgName} Membership?`;
      }
    },
    orgDescription() {
      return this.orgConfigs[this.orgType].description;
    },
    orgFeatures() {
      return this.orgConfigs[this.orgType].features;
    },
    purchaseUrl() {
      return this.orgConfigs[this.orgType].purchaseUrl;
    },
    helpUrl() {
      return this.orgConfigs[this.orgType].helpUrl;
    },
  },
  created() {
    this.getMemberships();
    // If we have a redirect, this is a standalone view (meaning we need an initial history state)
    // if we don't, we expect the parent vue component to manage initial state
    // todo clean this up. Hacking this in to get it working but it is not clean
    //  since this component should not be capable of knowing about parent state.
    history.replaceState(
      {
        showingForm: false,
        managerState: this.orgType,
      },
      '',
    );

    window.addEventListener('popstate', this.popStateHandler);
  },
  destroyed() {
    window.removeEventListener('popstate', this.popStateHandler);
  },
  methods: {
    popStateHandler(event) {
      if (event.state && 'showingForm' in event.state) {
        this.showForm = event.state.showingForm;
      }
    },
    toggleForm() {
      // If we are showing the form, just go backwards in time.
      // this is handled by the popstate handler above to show or hide the form
      if (this.showForm) {
        history.back();
      } else {
        this.showForm = !this.showForm;
        // Push state so we know what's up
        history.pushState(
          {
            showingForm: true,
            managerState: 'membership',
          },
          '',
        );
      }
    },
    readableExpires(expiresOn) {
      return moment(expiresOn).format('MMM Do YYYY');
    },
    badgeFor(membership) {
      if (moment().isSameOrBefore(membership.expires_on, 'day')) {
        return '<span class="badge badge-primary">Active</span>';
      }

      return '<span class="badge badge-danger">Expired</span>';
    },
    // This is hit when insurance-membership-formcreates a brand new usaw membership
    addMembership(membership) {
      this.memberships.push(membership);
      this.toggleForm();
      if (this.redirect) {
        window.location.assign(`/wrestlers/${this.wrestlerId}`);
      }
    },
    getMemberships() {
      const vm = this;
      const url = vm.$apiService[`${vm.orgType}Memberships`](this.wrestlerId);
      vm.loading = true;
      axios
        .get(url)
        .then((response) => {
          vm.loading = false;
          vm.memberships = response.data[`${vm.orgType}_memberships`];
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = `Error retrieving memberships ${error.toString()}`;
          vm.error = true;
        });
    },
    destroyID(membershipid) {
      const vm = this;
      // If this is a duplicate request, just return
      if (vm.loading === true) {
        return;
      }
      vm.loading = true;
      vm.error = false;

      const url = vm.$apiService[`${vm.orgType}Membership`](
        vm.wrestlerId,
        membershipid.id,
      );
      axios
        .delete(url)
        .then((response) => {
          vm.loading = false;
          vm.$notificationManager.emit(
            'show-toast',
            'Membership deleted',
            true,
          );
          // Remove it from our local knowledge
          vm.memberships = _.reject(vm.memberships, ['id', membershipid.id]);
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = 'Error deleting membership, please try again later or contact support@wrestlingiq.com';
          vm.error = true;
        });
    },
    confirmDelete(membershipid) {
      const vm = this;
      swal
        .fire({
          icon: 'warning',
          title: `Are you sure you want to delete ${this.orgName} #${
            membershipid.public_id
          } that expires on ${this.readableExpires(membershipid.expires_on)}?`,
          text: `This will remove the ${this.orgName} membership info from ${this.profileName} and regenerate their WIQ ID card.`,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, delete the membership',
          cancelButtonText: 'No, keep it',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            vm.destroyID(membershipid);
          }
        });
    },
  },
};
</script>
